import { URLData } from '@telia-common-components/shared';
import React from 'react';
import API from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
import { useInternetCalculator } from './InternetCalculatorProvider';
import { getCustomerIdFromAuth } from './util/InternetCalculatorUtil';
export const DEFAULT_SELECTED_PACKAGE_STATE = { url: '', productOfferingCode: '', hasForm: false, offer: undefined };
export const DEFAULT_MANDATES_STATE = { hasMandate: false, status: FetchStatus.UNINITIALIZED };
export const DEFAULT_STIPULATION_STATE = { stipulation: false, status: FetchStatus.UNINITIALIZED };
export const DEFAULT_CREDIT_STATE = {
    status: FetchStatus.UNINITIALIZED,
    creditAllowed: false,
    errorCodes: []
};
export const InternetNoticesProvider = (props) => {
    const [creditAllowedState, setCreditAllowedState] = React.useState(DEFAULT_CREDIT_STATE);
    const [hasLegalOrServiceMandateState, setHasLegalOrServiceMandateState] = React.useState(DEFAULT_MANDATES_STATE);
    const [representationStipulationState, setRepresentationStipulationState] = React.useState(DEFAULT_STIPULATION_STATE);
    const [selectedInternetPackage, setSelectedInternetPackage] = React.useState(DEFAULT_SELECTED_PACKAGE_STATE);
    const { auth } = useInternetCalculator();
    const urlData = new URLData({ ...props, lang: props.language });
    const offersApi = API(urlData.commonAPIBaseURL, { withCredentials: true, timeout: 20000 });
    const hasMandate = getCustomerIdFromAuth(auth) && hasLegalOrServiceMandateState.status === FetchStatus.FETCHED && hasLegalOrServiceMandateState.hasMandate;
    const fetchIsCreditAllowed = async () => {
        if (!getCustomerIdFromAuth(auth)) {
            setCreditAllowedState(DEFAULT_CREDIT_STATE);
            return;
        }
        if (!hasMandate) {
            setCreditAllowedState({ ...DEFAULT_CREDIT_STATE, status: FetchStatus.FETCHED });
            return;
        }
        try {
            setCreditAllowedState({ ...DEFAULT_CREDIT_STATE, status: FetchStatus.FETCHING });
            const response = await offersApi.get('/credit/is-credit-allowed');
            setCreditAllowedState({ status: FetchStatus.FETCHED, creditAllowed: response.data.creditAllowed, errorCodes: response.data.errorCodes });
        }
        catch (e) {
            setCreditAllowedState({ ...DEFAULT_CREDIT_STATE, status: FetchStatus.ERROR });
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
        }
    };
    const fetchHasLegalOrServiceMandate = async () => {
        if (!getCustomerIdFromAuth(auth)) {
            setHasLegalOrServiceMandateState({ status: FetchStatus.UNINITIALIZED, hasMandate: false });
            return;
        }
        try {
            setHasLegalOrServiceMandateState({ status: FetchStatus.FETCHING, hasMandate: false });
            const response = await offersApi.get('/mandate/has-legal-or-service');
            setHasLegalOrServiceMandateState({ status: FetchStatus.FETCHED, hasMandate: response.data });
        }
        catch (e) {
            setHasLegalOrServiceMandateState({ status: FetchStatus.ERROR, hasMandate: false });
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
        }
    };
    const fetchRepresentationStipulation = async () => {
        if (!getCustomerIdFromAuth(auth)) {
            setRepresentationStipulationState({ status: FetchStatus.UNINITIALIZED, stipulation: false });
            return;
        }
        try {
            setRepresentationStipulationState({ status: FetchStatus.FETCHING, stipulation: false });
            const response = await offersApi.get('/mandate/get-representation-stipulation');
            setRepresentationStipulationState({ status: FetchStatus.FETCHED, stipulation: response.data });
        }
        catch (e) {
            setRepresentationStipulationState({ status: FetchStatus.ERROR, stipulation: false });
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
        }
    };
    const hasCreditOrDebtErrors = () => !creditAllowedState.creditAllowed &&
        creditAllowedState.status === FetchStatus.FETCHED &&
        creditAllowedState.errorCodes.some((code) => ['RATING', 'DEBT_TELIA'].includes(code));
    const resetIsCreditAllowed = () => {
        setCreditAllowedState({ status: FetchStatus.UNINITIALIZED, creditAllowed: false, errorCodes: [] });
    };
    const isCreditAllowedFetching = () => {
        return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(creditAllowedState.status);
    };
    return (React.createElement(InternetNoticesContext.Provider, { value: {
            isSsoModalOpen: props.isSsoModalOpen,
            creditAllowedState,
            selectedInternetPackage,
            fetchIsCreditAllowed,
            fetchHasLegalOrServiceMandate,
            fetchRepresentationStipulation,
            resetIsCreditAllowed,
            hasCreditOrDebtErrors,
            isCreditAllowedFetching,
            setSelectedInternetPackage,
            hasLegalOrServiceMandateState,
            representationStipulationState
        } }, props.children));
};
const InternetNoticesContext = React.createContext({
    isSsoModalOpen: false,
    selectedInternetPackage: DEFAULT_SELECTED_PACKAGE_STATE,
    fetchHasLegalOrServiceMandate: () => undefined,
    fetchRepresentationStipulation: () => undefined,
    hasLegalOrServiceMandateState: DEFAULT_MANDATES_STATE,
    representationStipulationState: DEFAULT_STIPULATION_STATE,
    hasCreditOrDebtErrors: () => false,
    creditAllowedState: DEFAULT_CREDIT_STATE,
    fetchIsCreditAllowed: () => false,
    resetIsCreditAllowed: () => undefined,
    isCreditAllowedFetching: () => false,
    setSelectedInternetPackage: (_newState) => undefined
});
export const useInternetNotices = () => {
    return React.useContext(InternetNoticesContext);
};
